import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate, formatDisplayDateTime, formatDisplayTime, parseServerDate } from "src/utils/dateTime";
import { DateRangePicker } from "src/view/conponents/DateRangePicker";
import { Badge } from "@ui/badge/Badge";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Table } from "@ui/table/Table";
import { Typo } from "@ui/text/Typo";
import { useState } from "react";
import { TFeaturedComponent } from "src/Featuresmap";
import { GD } from "src/GD";
import { IPaymentSession } from "src/types/types";
import { IPagination, Pagination } from "./Pagination";
import { S_RAW_TEXT_POPUP } from "./popup/RawTextPopup";

type TPaymentSessionLogEntry = {
	type: string;
	text: unknown;
	timestamp: number;
	id?: number;
	count?: number;
};

type TSessionLogEntry = {
	id?: number;
	type: string;
	date: Date;
	text: string;
	count?: number;
	isError?: boolean;
	status?: string;
	level?: number;
	raw?: unknown;
};

const typeColors: Record<string, string> = {
	network: "#3B82F6",
	system: "#8B5CF6",
	data: "#06B6D4",
	antifraud: "#F59E0B",
	webhook: "#16A34A",
	error: "#DC2626",
};

type TFilterOption = {
	name: string;
	value: string;
};

type TDectaLogFilters = {
	dateFrom: string;
	dateTo: string;
	type: string;
	level: string;
};

const dectaLogPerPage = 20;

const logTypeOptions: TFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("network"); }, value: "network" },
	{ get name() { return msg("system"); }, value: "system" },
	{ get name() { return msg("data"); }, value: "data" },
	{ get name() { return msg("antifraud"); }, value: "antifraud" },
	{ get name() { return msg("webhook"); }, value: "webhook" },
];

const logLevelOptions: TFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("info"); }, value: "10" },
	{ get name() { return msg("warn"); }, value: "20" },
	{ get name() { return msg("error_d98ee0e5"); }, value: "30" },
	{ get name() { return msg("critical"); }, value: "40" },
];

const logLevelLabels: Record<number, string> = {
	10: "INFO",
	20: "WARN",
	30: "ERROR",
	40: "CRITICAL",
};

const logLevelColors: Record<number, string> = {
	10: "#3B82F6",
	20: "#F59E0B",
	30: "#DC2626",
	40: "#7F1D1D",
};

const formatTime = (date: Date) => formatDisplayDateTime(date);

const formatShortLogId = (id?: number) => {
	if (id === undefined) return "---";

	const value = String(id);
	return value.length > 5 ? "..." + value.slice(-5) : value;
};

const formatCount = (count?: number) => (count === undefined || count === null ? "---" : String(count));

const formatPaymentLogText = (value: unknown) => {
	if (typeof value === "string") {
		try {
			return JSON.stringify(JSON.parse(value));
		} catch {
			return value;
		}
	}
	return JSON.stringify(value);
};

const getLogText = (value: unknown) => {
	const text = formatPaymentLogText(value);
	if (text === undefined) return "";
	return text;
};

const getFullLogText = (entry: TSessionLogEntry) => {
	return JSON.stringify(
		entry.raw ?? {
			id: entry.id,
			type: entry.type,
			time: entry.date.toISOString(),
			count: entry.count,
			text: entry.text,
		},
		null,
		2,
	);
};

const getLogSummary = (entry: TSessionLogEntry) => {
	return [
		{ title: msg("id_3843971d"), value: entry.id !== undefined ? String(entry.id) : "---" },
		{ title: msg("type"), value: entry.type || "---" },
		{ title: msg("date_99c40ab4"), value: Number.isNaN(entry.date.getTime()) ? "---" : formatDisplayDate(entry.date, { withZone: false }), dateTime: entry.date },
		{ title: msg("time"), value: Number.isNaN(entry.date.getTime()) ? "---" : formatDisplayTime(entry.date), dateTime: entry.date },
		{ title: msg("count"), value: formatCount(entry.count) },
		entry.level !== undefined ? { title: msg("level"), value: String(entry.level) } : null,
	].filter((item): item is { title: string; value: string; dateTime?: Date } => Boolean(item));
};

const SessionLogList = ({ entries }: { entries?: TSessionLogEntry[] }) => {
	useLocale();
	if (entries && entries.length === 0) {
		return (
			<VBox center sx={{ padding: "40px 16px" }}>
				<Typo variant="medium" color="#9ba3b8" align="center">{msg("noSessionLogsAvailable")}</Typo>
			</VBox>
		);
	}

	if (!entries?.length) return null;

	const headerLabels: Record<string, string> = {
		type: "Type",
		level: "Level",
		count: "Count",
		date: "Time / ID",
		text: msg("message_2f77668a"),
	};
	const hasLevel = entries.some(log => log.level !== undefined);
	const headers = [
		{ value: "type", gridColumn: "110px" },
		...(hasLevel ? [{ value: "level", gridColumn: "110px" }] : []),
		{ value: "count", gridColumn: "80px" },
		{ value: "date", gridColumn: "max-content" },
		{ value: "text", gridColumn: "minmax(360px, 1fr)" },
	];
	const order = hasLevel ? ["type", "level", "count", "date", "text"] : ["type", "count", "date", "text"];
	const gridTemplateColumns = hasLevel
		? "110px 110px 80px max-content minmax(360px, 1fr)"
		: "110px 80px max-content minmax(360px, 1fr)";
	const openLog = (log: TSessionLogEntry) => {
		S_RAW_TEXT_POPUP.invoke({
			title: msg("templateSessionLog_7fb78d46", { v0: log.id ?? "" }).trim(),
			value: getFullLogText(log),
			summary: getLogSummary(log),
		});
	};

	return (
		<Box sx={{ flex: "1 1 100%", minWidth: 0, boxSizing: "border-box" }}>
			<Box sx={{ overflowX: "auto" }}>
				<Table<TSessionLogEntry>
					headPadding="8px 12px"
					cellPadding="8px 12px"
					sx={{ minWidth: "680px" }}
					headSx={{ backgroundColor: "#d1dbe6" }}
					headers={headers}
					order={order as Array<keyof TSessionLogEntry>}
					gridTemplateColumns={gridTemplateColumns}
					headerFactory={value => ({
						content: (
							<Typo
								variant="medium"
								sx={{
									fontWeight: 700,
									fontFamily: "monospace",
									fontSize: "11px",
									textTransform: "uppercase",
									letterSpacing: "0.03em",
								}}>
								{headerLabels[String(value)] ?? String(value)}
							</Typo>
						),
					})}
					onRowClick={openLog}
					rowSx={log =>
						log.isError
							? {
									"& > div": { backgroundColor: "#FEF2F2" },
									"&:hover > div": { backgroundColor: "#FEE2E2" },
								}
							: undefined
					}
					cellFactory={(log, _cell, _index, _key, name) => {
						if (name === "type") {
							return {
								content: <Badge label={log.type.toUpperCase()} color={typeColors[log.type] ?? "#9CA3AF"} />,
							};
						}
						if (name === "level") {
							const level = log.level;
							return {
								content:
									level === undefined ? (
										<Typo variant="medium">---</Typo>
									) : (
										<Badge label={logLevelLabels[level] ?? String(level)} color={logLevelColors[level] ?? "#9CA3AF"} />
									),
							};
						}
						if (name === "count") {
							return {
								content: <Typo variant="medium" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{formatCount(log.count)}</Typo>,
							};
						}
						if (name === "date") {
							return {
								content: (
									<VBox gap="2px">
										<Typo variant="medium" sx={{ whiteSpace: "nowrap" }}><DateTimeTooltip value={log.date}>{formatTime(log.date)}</DateTimeTooltip></Typo>
										<Typo variant="caption" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>{formatShortLogId(log.id)}</Typo>
									</VBox>
								),
							};
						}
						return {
							content: (
								<Typo
									variant="medium"
									sx={{
										display: "block",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										fontFamily: "monospace",
										color: log.isError ? "#DC2626" : undefined,
									}}>
									{log.text}
								</Typo>
							),
							sx: { minWidth: 0, overflow: "hidden" },
						};
					}}
					data={entries}
				/>
			</Box>
		</Box>
	);
};

export const PaymentSessionLog: TFeaturedComponent<{ session: IPaymentSession }> = ({ session }) => {
	useLocale();
	const [response] = GD.REQ_API.useRequest(null, { method: "session/log", data: { id: session.id } });
	const entries = Array.isArray(response?.data)
		? response.data.map((row: TPaymentSessionLogEntry) => ({
				id: row.id,
				type: row.type,
				date: parseServerDate(row.timestamp) ?? new Date(NaN),
				text: getLogText(row.text),
				count: row.count,
				isError: row.type === "error",
				status: row.type,
				raw: row,
			}))
		: undefined;

	return <SessionLogList entries={entries} />;
};

PaymentSessionLog.feature = "payment_log_section";

const DectaLog = ({ paymentId, showFilters = false }: { paymentId?: number | string; showFilters?: boolean }) => {
	useLocale();
	const [currentPage, setCurrentPage] = useState(0);
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [typeFilter, setTypeFilter] = useState<TFilterOption>(logTypeOptions[0]);
	const [levelFilter, setLevelFilter] = useState<TFilterOption>(logLevelOptions[0]);

	const currentFilters = (): TDectaLogFilters => ({
		dateFrom,
		dateTo,
		type: typeFilter.value,
		level: levelFilter.value,
	});
	const getRequestData = (page: number, filters: TDectaLogFilters = currentFilters()) => ({
		method: "processing/log/get",
		data: {
			payment_id: paymentId,
			page,
			limit: dectaLogPerPage,
			...(filters.dateFrom ? { date_from: filters.dateFrom } : {}),
			...(filters.dateTo ? { date_to: filters.dateTo } : {}),
			...(filters.type ? { type: filters.type } : {}),
			...(filters.level ? { level: Number(filters.level) } : {}),
		},
	});

	const [response, reload, _reset, busy] = GD.REQ_API.useRequest(null, getRequestData(currentPage));
	const responseItems = Array.isArray(response?.data?.items) ? response.data.items : Array.isArray(response?.data) ? response.data : undefined;
	const entries = responseItems
		? responseItems.map((log: ISessionLogEntry) => ({
				id: log.id,
				type: log.type,
				date: parseServerDate(log.created_at) ?? new Date(NaN),
				text: getLogText(log.text),
				count: log.count,
				isError: log.level >= 30,
				level: log.level,
				status: log.level >= 30 ? "error" : log.type,
				raw: log,
			}))
		: undefined;
	const serverPagination = response?.data?.pagination as IPagination | undefined;
	const pagination = serverPagination;

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		reload(getRequestData(page));
	};
	const handleSearch = () => {
		setCurrentPage(0);
		reload(getRequestData(0));
	};
	const handleReset = () => {
		const emptyFilters: TDectaLogFilters = { dateFrom: "", dateTo: "", type: "", level: "" };
		setDateFrom("");
		setDateTo("");
		setTypeFilter(logTypeOptions[0]);
		setLevelFilter(logLevelOptions[0]);
		setCurrentPage(0);
		reload(getRequestData(0, emptyFilters));
	};

	return (
		<VBox sx={{ flex: 1, minWidth: 0 }}>
			{showFilters && (
				<>
					<HBox gap="8px" sx={{ flexWrap: "wrap", alignItems: "end" }}>
						<DateRangePicker timeZone="UTC"
							style={{ width: "290px", maxWidth: "100%" }}
							label={msg("dateRange")}
							from={dateFrom}
							to={dateTo}
							allowClear
							onChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
						/>
						<Select<TFilterOption>
							sx={{ minWidth: "160px", maxWidth: "220px" }}
							size="small"
							label={msg("type")}
							value={typeFilter}
							values={logTypeOptions}
							onChange={value => setTypeFilter(logTypeOptions.find(option => option.value === String(value)) ?? logTypeOptions[0])}
						/>
						<Select<TFilterOption>
							sx={{ minWidth: "160px", maxWidth: "220px" }}
							size="small"
							label={msg("level")}
							value={levelFilter}
							values={logLevelOptions}
							onChange={value => setLevelFilter(logLevelOptions.find(option => option.value === String(value)) ?? logLevelOptions[0])}
						/>
						<HBox gap="8px" sx={{ marginLeft: "auto" }}>
							<Button onClick={handleReset} variant="secondarySmall" title={msg("reset")} />
							<Button onClick={handleSearch} variant="primarySmall" title={msg("search")} />
						</HBox>
					</HBox>
					<Spacer size="10px" />
				</>
			)}

			<ProgressBar busy={busy} />
			{response?.error ? (
				<Box sx={{ padding: "20px" }}>
					<Typo variant="medium" sx={{ color: "#DC2626" }}>
						{response.error.message ?? msg("errorLoadingLogs")}
					</Typo>
				</Box>
			) : (
				!busy && <SessionLogList entries={entries} />
			)}

			<Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", padding: "10px 0" }}>
				<Pagination pagination={pagination} onPageChange={handlePageChange} />
				<Spacer grow />
				<Button onClick={() => reload(getRequestData(currentPage))} variant="primarySmall" title={msg("refresh")} />
			</Box>
		</VBox>
	);
};

export const DectaSessionLog = ({ data }: { data?: IDectaSessionFull }) => {
	useLocale();
	return <DectaLog paymentId={data?.session?.ssid} />;
};

export const DectaLogs = () => {
	useLocale();
	return <DectaLog paymentId={1} showFilters />;
};
